import React from "react";
import { PageHero, CTA } from "@/components/PageHero";
import { STATS } from "@/data/site";
import { Award, Target, Users, Zap } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

export default function About() {
  useSEO({
    title: "About Us — Easyworks Industries Pvt. Ltd.",
    description: "Meet Easyworks Industries — six specialised technology companies under one roof, headquartered in India with a regional office in the UAE.",
  });
  return (
    <div data-testid="about-page">
      <PageHero
        title="About Easyworks Industries"
        subtitle="A group of technology companies driving digital transformation for businesses across India — through innovation, automation and expert project management."
      />
      <section className="py-20">
        <div className="container mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="section-label">Who We Are</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5" style={{ fontFamily: "Outfit" }}>One family. Six specialised companies. Endless possibilities.</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Easyworks Industries Pvt. Ltd. brings together six focused technology companies under one umbrella — Digital, Software, AI, Cloud, Creative and Marketing. Each company is led by domain experts and works together to deliver end-to-end solutions.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Headquartered in India with a regional office in the UAE, we serve clients across healthcare, education, real estate, manufacturing, retail, logistics and more.
            </p>
          </div>
          <div>
            <img src="https://images.pexels.com/photos/14527417/pexels-photo-14527417.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Office" className="rounded-xl object-cover w-full h-[380px]" />
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50/60 border-y border-slate-100">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {STATS.map((s, i) => (
              <div key={i} className="p-6 rounded-xl bg-white border border-slate-200 text-center">
                <div className="text-3xl font-extrabold text-[#2563EB]" style={{ fontFamily: "Outfit" }}>{s.value}</div>
                <div className="text-[12px] text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="text-center mb-12">
            <span className="section-label">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>What drives us every day</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Target, t: "Purpose-Driven", d: "Every solution we build solves a real business problem." },
              { icon: Zap, t: "Innovation First", d: "We embrace AI, automation and modern engineering." },
              { icon: Users, t: "Client Success", d: "We measure success by the results our clients achieve." },
              { icon: Award, t: "Quality Focus", d: "Industry best practices, security and long-term reliability." },
            ].map(({ icon: Icon, t, d }, i) => (
              <div key={i} className="p-6 rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-900/5 transition-all">
                <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-[#2563EB]" /></div>
                <div className="font-semibold text-slate-900 mb-1.5" style={{ fontFamily: "Outfit" }}>{t}</div>
                <div className="text-[13px] text-slate-500 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CTA />
    </div>
  );
}
